import FadeIn from "@/components/brandRefresh/common/FadeInEffect";
import Seo from "@/components/elements/seo";
import Layout from "@/components/layout";
import React, { useEffect, useState } from "react";
import { getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";
import { global } from "utils/pageConstants";
import dynamic from "next/dynamic";
import { CircleOutlined } from "@mui/icons-material";
import HowItWorkComponent from "@/components/brandRefresh/how-it-work-component";
import AtFingerTipsComponent from "@/components/brandRefresh/at-fingertips-component";
import NewToCredit from "@/components/brandRefresh/new-to-credit";
import SimplifyCreditComponent from "@/components/brandRefresh/simplify-credit-component";
import WeAreOnMoneyComponent from "@/components/brandRefresh/were-on-money-component";
import NavbarComp from "@/components/elements/navbar";
import Head from "next/head";

const HeroSection = dynamic(
  () => import("@/components/brandRefresh/hero-section"),
  { ssr: false },
);

function Landing({ metadata, deviceType }) {
  const [activeIndex, setActiveIndex] = useState("section1");
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [sectionCount, setSectionCount] = useState(0);
  const metadataWithDefaults = {
    ...global().attributes.metadata,
    ...metadata,
  };

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveIndex(entry.target.id);
          }
        });
      },
      {
        rootMargin: "0px",
        threshold: 0.5,
      },
    );
    const footerObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsFooterVisible(true);
          } else {
            setIsFooterVisible(false);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );
    const sections = document.querySelectorAll('section[id^="section"]');
    setSectionCount(sections.length);
    const footerElement = document.getElementById("landing-footer");
    sections.forEach(s => sectionObserver.observe(s));
    if (footerElement) {
      footerObserver?.observe(footerElement);
    }
    return () => {
      sections.forEach(s => sectionObserver.unobserve(s));
      if (footerElement) {
        footerObserver?.unobserve(footerElement);
      }
    };
  }, []);

  const { navbar } = global().attributes;

  return (
    <Layout
      showHeaderLayout={false}
      global={global()}
      showHeader={activeIndex === "section1"}
    >
      <Head>
        <link
          rel="preload"
          href="/images/desktop-background.svg"
          as="image"
          type="image/svg+xml"
        />
        <link
          rel="preload"
          href="/images/mobile-background.svg"
          as="image"
          type="image/svg+xml"
        />
      </Head>
      {/* Add meta tags for SEO*/}
      <Seo metadata={metadataWithDefaults} />

      {/* Uses next <Image/> to optimise loading of the background asset */}
      <div className="h-full -z-50 w-full fixed gradient-bg-primary" />
      <div
        className={`md:snap-y md:snap-proximity ${
          isFooterVisible ? "overflow-hidden" : "scroll-container"
        } `}
      >
        <div className="w-full">
          <NavbarComp navbar={navbar} showNav />
        </div>
        <section
          id="section1"
          className="w-full min-h-screen py-10 pt-20 flex scroll-area flex-col"
        >
          <WeAreOnMoneyComponent />
        </section>
        <section
          id="section2"
          className="w-full xs:py-12 min-h-screen py-10  flex my-auto bg-onmoPurple-100 rounded-[40px] lg:my-auto scroll-area"
        >
          <SimplifyCreditComponent />
        </section>
        <section
          id="section3"
          className="w-full  min-h-screen py-10  m-auto flex flex-col justify-center scroll-area text-white"
        >
          <AtFingerTipsComponent deviceType={deviceType} />
        </section>
        <section
          id="section4"
          className="w-full bg-onmoPurple-100 min-h-screen py-10  rounded-[40px] flex justify-center scroll-area"
        >
          <div className="max-w-[968px] 2xl:max-w-[1070px] m-auto items-center text-onmoText my-10 lg:my-auto">
            <div className="flex flex-col items-center text-center px-6 lg:px-0">
              <FadeIn>
                <h2 className="text-size-6 lg:text-6xl 2xl:text-7xl mb-2 font-normal font-ppNeue">
                  How it works
                </h2>
              </FadeIn>

              <FadeIn>
                <p className="text-base lg:text-xl 3xl:text-2xl lg:mb-spacing-14 mb-spacing-12 font-normal">
                  Getting started with Onmo is quick and simple.{" "}
                  <br className="hidden lg:block" />
                  Just follow these steps to apply, set up, and start using your
                  credit card!
                </p>
              </FadeIn>
              <HowItWorkComponent />
              <div className="mt-spacing-12 lg:mt-spacing-14">
                <NewToCredit deviceType={deviceType} />
              </div>
            </div>
          </div>
        </section>
        <section
          id="section5"
          className="w-full min-h-screen py-10  m-auto flex flex-col justify-center scroll-area text-white"
        >
          <HeroSection deviceType={deviceType} />
        </section>
        <div
          className={`hidden md:flex flex-col gap-1 fixed top-[43%] right-3 ${
            isFooterVisible ? "!hidden" : ""
          } `}
        >
          {Array(sectionCount)
            .fill(0)
            .map((_, i) => (
              <CircleOutlined
                key={i}
                className={`!w-2 !h-2 ${
                  activeIndex.includes(i + 1)
                    ? "text-onmoPurple-300  bg-onmoPurple-300"
                    : "text-onmoPurple-500  bg-onmoPurple-500"
                }  !border-0 rounded-full m-1`}
              />
            ))}
        </div>
      </div>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);
  context.res.setHeader("Cache-Control", "public, max-age=300, s-maxage=600");
  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `Onmo | We're on the money`,
  };

  const userAgent = context.req.headers["user-agent"];
  let deviceType = "desktop";
  if (/mobile/i.test(userAgent)) {
    deviceType = "mobile";
  } else if (/tablet/i.test(userAgent) || /iPad/i.test(userAgent)) {
    deviceType = "tablet";
  }

  return {
    props: {
      metadata,
      global: globalLocale.data,
      deviceType: deviceType,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}

export default Landing;
